/**
 * 광고 삽입 계약 (v21). **일반(드라마) 채널의 광고 브레이크 하나가 여기 전부 있다.**
 *
 * 갈리는 것이 셋이다 — **언제 자를지**는 채널의 `AdPolicy` 가, **무엇을 틀지**는 광고
 * 서버가 VAST 로, **어디서 자를지**는 수신기가 청크 경계로 정한다 (`docs/45` §3·§7·§8).
 *
 * **산술이 이 패키지에 있는 것이 결정이다** (`docs/45` D15). 같은 값을 셋이 필요로 한다 —
 * 리시버(무장) · `bloom-console`(광고량 표시) · `ad-server`(편성 검증). 두 벌이 되면
 * **「콘솔이 말하는 광고량과 실제로 나가는 양이 다르다」**가 되고, 그 어긋남은 **아무
 * 오류도 내지 않는다** (절대 규칙 57).
 */
// ---------------------------------------------------------------------------
// 1. 열거값
// ---------------------------------------------------------------------------
/**
 * 브레이크의 종류 셋. **「어디서」이지 「왜」가 아니다** (`docs/45` D19).
 *
 * 시청자 조작(건너뛰기·목록 선택)에 새 종류를 만들지 않는다 — Ad Request 의 `pod`
 * 파라미터 값이 늘어 **상용 서버가 모르는 값**이 되고, 콘솔 폼에는 조작마다 입력란이
 * 하나씩 붙는다. 조작은 `AdTransitionPolicy` 로 **이 셋 중 하나를 가리킬 뿐이다.**
 */
export const AD_BREAK_KINDS = [
    'preroll',
    'midroll',
    'between_episodes',
    /**
     * 2026-10-29 — **혼합 채널의 라이브 이벤트 앞뒤**(`docs/45` §18). 「어디서」의 두 자리가 새로 생겼다 — 라이브가 시작되기
     * 직전(시작 순간에 끝나게)과 라이브가 끝난 직후(녹화물로 돌아가기 전). 조작이 아니라 자리이므로 D19 와 어긋나지 않는다.
     */
    'live_before',
    'live_after',
];
export function isAdBreakKind(value) {
    return AD_BREAK_KINDS.includes(value);
}
/**
 * 시청자가 조작했을 때 어떤 브레이크를 여는가. **값이 곧 어느 길이를 쓸지다.**
 *
 * `midroll` 이 여기 없다. 조작은 회차의 경계에서 일어나므로 회차 **안**의 시점을 가리킬
 * 수 없다 — 있으면 `midrollIntervalSec` 과 무관한 「중간 광고」가 생긴다.
 */
export const AD_TRANSITION_POLICIES = ['none', 'preroll', 'between_episodes'];
export function isAdTransitionPolicy(value) {
    return AD_TRANSITION_POLICIES.includes(value);
}
/**
 * ★ **광고의 언어** (v21 확장). 채널이 이 값을 말하고, 광고 서버가 **그 언어의 소재만**
 * 후보로 삼는다 (`docs/45` D26~D28).
 *
 * ── 이것은 시청자의 언어가 아니다 ───────────────────────────
 *
 * 축이 둘이고 **섞으면 안 된다.**
 * - `BloomDeviceRegisterRequest.locale` — **이 시청자가 보는 화면**의 언어. 알림 문구가
 *   그것으로 만들어진다 (`docs/22` §8)
 * - **이것** — **이 채널에 붙는 광고**의 언어. 영어 드라마 채널은 한국어로 앱을 보는
 *   시청자에게도 영어 광고를 낸다
 *
 * ── `@apm/i18n` 을 import 하지 않는다 ───────────────────────
 *
 * 값 집합은 `Locale` 과 같지만 **계약이 UI 패키지에 의존하면 층이 뒤집힌다** —
 * `bloom.ts` 의 `locale?: 'ko' | 'en'` 이 같은 이유로 값을 여기 적었고, 그 주석이 근거를
 * 갖는다. 세 번째 언어가 생기면 그 값을 여기 넣는 순간 `pnpm typecheck` 가 깨진다.
 */
export const AD_LANGUAGES = ['ko', 'en'];
export function isAdLanguage(value) {
    return AD_LANGUAGES.includes(value);
}
// ---------------------------------------------------------------------------
// 6. 상수
// ---------------------------------------------------------------------------
/** 예약형 요청 — 미드롤 · 회차 사이. 컷 지점 이 초 전에 미리 받아 둔다 */
export const AD_REQUEST_LEAD_SEC = 20;
/** 예약형 요청의 타임아웃. 재생 뒤에서 도므로 길어도 된다 */
export const AD_REQUEST_TIMEOUT_MS = 10_000;
/**
 * **즉시형 요청** — 프리롤 · 건너뛰기 · 목록 선택. **재생을 막으므로 짧다.**
 *
 * 여기에 예약형 타임아웃(10초)을 쓰면 **프리롤에서 검은 화면이 10초** 가고, 그것이 채널
 * 진입의 첫인상이다 (`docs/45` §14 의 6).
 */
export const AD_REQUEST_BLOCKING_TIMEOUT_MS = 3_000;
/**
 * **스킵 재보충**(`docs/45` §17)의 타임아웃. 건너뛴 순간에 묻고, 마지막 광고를 건너뛰었으면 그 답을 기다리는 동안
 * 건너뛴 광고가 계속 나간다 — 즉시형(3초)보다도 짧다. 늦은 답은 쓸모가 없다(그사이 브레이크가 끝난다).
 */
export const AD_REFILL_TIMEOUT_MS = 1_500;
/** 한 브레이크(pod)에 넣을 수 있는 광고의 최대 편수 */
export const AD_POD_MAX = 10;
/**
 * VAST `<Extension>` 의 `type` 속성. **문자열 하나가 계약이다** (`docs/45` §7.4).
 *
 * 표준에 자리가 없는 것만 여기로 싣는다. **이 Extension 이 없어도 광고는 재생되고 구매
 * 버튼은 산다** — 딥링크 대신 웹 랜딩으로 갈 뿐이고, 그 강등을 `AdItem.handoffSource` 가
 * 드러낸다 (절대 규칙 54).
 */
export const AD_HANDOFF_EXTENSION_TYPE = 'apm/handoff';
// ---------------------------------------------------------------------------
// 7. 산술 일곱 — **한 벌이다** (D15 · 절대 규칙 57)
// ---------------------------------------------------------------------------
/**
 * 이 회차 안의 중간 광고 시점들. **정책만으로 정해진다 — 청크를 모른다** (절대 규칙 52).
 * 어디서 자를지는 수신기의 `core/chunk-align.ts` 가 정한다.
 *
 * **`t < itemDurationSec` 이다.** `<=` 로 두면 길이가 간격의 정확한 배수인 회차에서
 * **끝에서 중간 광고가 한 번 더** 나고 곧바로 회차 사이 브레이크가 온다 —
 * **그 회차에서만** 재현된다 (`docs/45` §14 의 3).
 *
 * ── ★ 꼬리에 붙는 컷은 버린다 (실기가 밟았다) ─────────────────
 *
 * **`<` 만으로는 모자랐다.** 241초 회차에 간격 120이면 컷이 `120`·`240` 이고 **뒤에 남는
 * 콘텐츠가 1초**다. 그 컷은 이름만 「중간」이지 실제로는 회차 끝이고, 시청자에게는
 * **「다음 회차를 눌렀는데 30초 동안 광고만 나오고 아무 일도 안 일어나는」** 것으로
 * 보인다 — 광고가 끝나도 돌아갈 본편이 없어 곧바로 다음 회차로 넘어가기 때문이다.
 * 2026-08-24 무대에서 그대로 났다.
 *
 * **그래서 「남는 콘텐츠가 브레이크보다 짧으면」 그 컷을 버린다.** 그것은 미드롤이 아니라
 * **일찍 온 회차 사이 브레이크**이고, 곧이어 진짜 회차 사이 브레이크가 또 오면 시청자는
 * 20초 보려고 60초를 본다.
 *
 * **정책값으로 막지 않는 이유가 이것이다.** 안전한 간격은 **회차 길이마다 다르고**
 * 편성이 바뀌면 또 달라진다 — 운영자가 매번 역산하게 두면 언젠가 틀리고, 그 실수는
 * 화면에 「광고가 좀 많네」로만 보인다. 산술이 한 벌이므로(D15 · 절대 규칙 57) 여기서
 * 버리면 **리시버·콘솔·광고 부하 표시가 한꺼번에** 같은 답을 낸다.
 *
 * **루프가 간격마다 반복한다** — 회차가 간격의 두 배를 넘으면 중간 광고가 둘이다.
 *
 * v38 CS13 — **ONAIR 채널도 이 함수를 부른다**(`onairMidrollCuePoints` · 서버가 회차를 자르는 자리). 그래서 받는 것을
 * 실제로 읽는 두 칸으로 좁혔다 — 동작은 한 글자도 같다(Flower 의 벡터가 그대로다 · 절대 규칙 95).
 */
export function midrollCuePoints(policy, itemDurationSec) {
    if (policy.midrollIntervalSec <= 0 || policy.midrollDurationSec <= 0)
        return [];
    const points = [];
    for (let t = policy.midrollIntervalSec; t < itemDurationSec; t += policy.midrollIntervalSec) {
        // ★ 뒤에 남는 콘텐츠가 브레이크보다 짧으면 그 컷은 미드롤이 아니다. 위 ★ 참조.
        if (itemDurationSec - t < policy.midrollDurationSec)
            break;
        points.push(t);
    }
    return points;
}
/**
 * ★ **간격이 회차 길이와 안 맞아 버려진 컷의 수** (실기 뒤 신설).
 *
 * `midrollCuePoints` 가 꼬리 컷을 조용히 버리므로 **운영자는 자기가 정한 간격이 그 회차에
 * 몇 번 나는지 알 수 없다.** 간격 120 에 241초 회차면 두 번을 기대하고 한 번을 받는다 —
 * 결과는 옳지만 **왜 그런지는 화면 어디에도 없다.**
 *
 * **산술을 콘솔에 다시 쓰지 않으려고 여기 둔다** (D15 · 절대 규칙 57). 두 벌이 되면
 * 「콘솔이 말하는 광고량과 실제로 나가는 양이 다르다」가 되고 그 어긋남은 조용하다.
 */
export function droppedTailCueCount(policy, itemDurationSec) {
    if (policy.midrollIntervalSec <= 0 || policy.midrollDurationSec <= 0)
        return 0;
    // 꼬리 규칙이 없었다면 몇 개였을까 — `t < itemDurationSec` 만 적용한 수.
    const naive = Math.max(0, Math.ceil(itemDurationSec / policy.midrollIntervalSec) - 1);
    return naive - midrollCuePoints(policy, itemDurationSec).length;
}
/** 종류 → 길이. **길이를 아는 자리가 여기 하나다** */
export function breakDurationSec(policy, kind) {
    if (kind === 'preroll')
        return policy.preRollSec;
    if (kind === 'between_episodes')
        return policy.betweenEpisodesSec;
    if (kind === 'live_before')
        return policy.liveBeforeSec;
    if (kind === 'live_after')
        return policy.liveAfterSec;
    return policy.midrollDurationSec;
}
/**
 * 조작 정책 → 종류. `none` 이면 `null` 이고 브레이크가 열리지 않는다.
 *
 * 조작에 **새 종류를 만들지 않는 것**이 이 함수의 존재 이유다 (`docs/45` D19).
 */
export function transitionBreakKind(policy, trigger) {
    const value = trigger === 'skip' ? policy.onSkip : policy.onSelect;
    return value === 'none' ? null : value;
}
/**
 * 이 회차의 **실제 소요 시간**(초) — **벽시계 축**이다. 콘텐츠 + 그 안의 중간 광고 +
 * 뒤따르는 회차 사이 브레이크.
 *
 * **프리롤은 넣지 않는다** — 회차의 것이 아니라 채널 진입의 것이고, 회차마다 세면 채널
 * 전체 길이가 **회차 수만큼** 부풀려진다.
 *
 * **이 값을 이어보기·청크·진행 보고에 쓰지 말 것** (절대 규칙 57). 광고가 짧게 나온 날
 * **엉뚱한 데서 시작한다** — 같은 회차인데 날마다 다르게 틀린다 (`docs/45` §14 의 11).
 *
 * **정책 길이로 세므로 상한이다.** 광고 서버가 덜 채울 수 있어 콘솔은 「최대」라고 쓴다.
 * 실측은 `ad_server.Impression` 이 낸다 (`docs/45` §4.4).
 */
export function effectiveDurationSec(policy, itemDurationSec) {
    if (policy === null)
        return itemDurationSec;
    const midrolls = midrollCuePoints(policy, itemDurationSec).length;
    return itemDurationSec + midrolls * policy.midrollDurationSec + policy.betweenEpisodesSec;
}
/** 이 회차에 붙는 광고 초. `effectiveDurationSec` 과 콘텐츠 축의 차이다 */
export function adSecondsFor(policy, itemDurationSec) {
    if (policy === null)
        return 0;
    return effectiveDurationSec(policy, itemDurationSec) - itemDurationSec;
}
/**
 * 시간당 광고 초 — 광고 부하(ad load). 콘솔이 경고를 그리는 근거다.
 *
 * **서버가 막지 않고 콘솔이 경고한다** — 회차 길이가 정책 검증 시점에 없기 때문이다.
 * 막으면 「어제는 저장되던 정책이 오늘은 안 된다」가 되고 그 이유가 화면에 드러나지 않는다
 * (`docs/45` §4.5). 눈금(720초)은 사양이 아니므로 **콘솔에만** 둔다.
 */
export function adLoadSecPerHour(policy, itemDurationSec) {
    const effective = effectiveDurationSec(policy, itemDurationSec);
    if (effective <= 0)
        return 0;
    return Math.round((adSecondsFor(policy, itemDurationSec) / effective) * 3600);
}
//# sourceMappingURL=ad.js.map